import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  BackButton,
  Box,
  Button,
  Center,
  Column,
  FloatingContainer,
  Row,
  Screen,
  Text,
} from '~/commonComponents';
import { usePostPermValuePropViewed } from '~/domain/PermanentJobs/useCases/usePostPermValuePropViewed';
import HowItWorks01 from '~/images/perm/how-it-works-01.svg';
import HowItWorks02 from '~/images/perm/how-it-works-02.svg';
import HowItWorks03 from '~/images/perm/how-it-works-03.svg';
import UserGroupIcon from '~/images/perm/user-group-icon.svg';
import { firePermValuePropEmailLinkClicked } from '~/posthogs/events';
import { Colors } from '~/themes/colors';
import { HowItWorksCard } from './components/HowItWorksCard';

const HOW_IT_WORKS_CARDS = [
  {
    icon: HowItWorks01,
    title: 'Reach Professionals Today',
    description: 'Your role is pushed to licensed dental professionals the moment you post.',
  },
  {
    icon: HowItWorks02,
    title: 'Hassle-Free Scheduling',
    description:
      'Skip the back-and-forth. Candidates request interviews and show up at your office.',
  },
  {
    icon: HowItWorks03,
    title: 'Hire on Your Terms',
    description: 'Select top talent from the largest network of verified dental professionals.',
  },
];

export const ValuePropositionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: postPermValuePropViewed } = usePostPermValuePropViewed();

  useEffect(() => {
    postPermValuePropViewed();
  }, [postPermValuePropViewed]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get('utm_source') === 'email' && params.get('utm_medium') === 'perm_value_prop') {
      firePermValuePropEmailLinkClicked();
    }
  }, [location.search]);

  const handleStartHiring = () => {
    navigate('/permanent-jobs/postings/create');
  };

  return (
    <Screen>
      <FloatingContainer flex={1} borderRadius={4} paddingBlock={48} paddingInline={16}>
        <Row position="relative" alignItems="center" justifyContent="center">
          <Box position="absolute" left={0} data-testid="value-prop-back-button">
            <BackButton />
          </Box>
        </Row>

        <Center marginTop={24}>
          <Column alignItems="center" gap={32} maxWidth={800}>
            <Column alignItems="center" gap={8}>
              <Text bold fontSize={32} color={Colors.neutral_600} textAlign="center">
                Your Next Permanent Hire is Already on GoTu.
              </Text>
              <Text fontSize={16} color={Colors.neutral_500} textAlign="center">
                The best dental professionals aren&apos;t on job boards, they&apos;re already
                working.
                <br />
                We put your role directly in front of them.
              </Text>
            </Column>

            <Column gap={24} alignItems="center" width="100%">
              <Text bold fontSize={24} color={Colors.primary_500} textAlign="center">
                How It Works
              </Text>
              <Row alignItems="stretch" width="100%">
                {HOW_IT_WORKS_CARDS.map((card, index) => (
                  <React.Fragment key={card.title}>
                    {index > 0 && (
                      <div
                        style={{
                          backgroundColor: Colors.neutral_100,
                          width: 1.5,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <HowItWorksCard
                      icon={card.icon}
                      title={card.title}
                      description={card.description}
                    />
                  </React.Fragment>
                ))}
              </Row>
            </Column>

            <Row
              alignItems="center"
              backgroundColor={Colors.primary_50}
              borderRadius={8}
              gap={8}
              padding="12px 24px"
              width="100%"
            >
              <img
                src={UserGroupIcon}
                alt="professionals"
                style={{ width: 24, height: 24, flexShrink: 0 }}
              />
              <Text fontSize={18} color={Colors.neutral_600}>
                200,000+ dental professionals on GoTu. Let them know your office is hiring today.
              </Text>
            </Row>

            <Button
              data-testid="value-prop-start-hiring-button"
              dataCy="value-prop-start-hiring-button"
              isLoading={false}
              style={{
                backgroundColor: Colors.primary_500,
                borderRadius: 25,
                height: 48,
                marginTop: 0,
                width: 296,
              }}
              text="Start Hiring"
              onClick={handleStartHiring}
            />
          </Column>
        </Center>
      </FloatingContainer>
    </Screen>
  );
};
